import { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { ShieldPlus, Shield, Pencil, Trash2 } from "lucide-react";
import { callApi } from "../api/api";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";

export default function Roles() {
  const { instance, accounts } = useMsal();

  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedRole, setSelectedRole] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    role_name: "",
    role_code: "",
    role_description: "",
  });

  const [errors, setErrors] = useState({});

  /* ================= SEARCH DEBOUNCE ================= */

  const [isNarrowToolbar, setIsNarrowToolbar] = useState(false);
  const [isAddTooltipVisible, setIsAddTooltipVisible] = useState(false);
  const addTooltipTouchTimer = useRef(null);


  useEffect(() => {
    return () => {
      if (addTooltipTouchTimer.current) {
        clearTimeout(addTooltipTouchTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowToolbar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  /* ================= LOAD ================= */

  const loadRoles = useCallback(
    async (searchText = "", currentPage = 1) => {
      try {
        setLoading(true);

        const finalSearch =
          searchText !== undefined && searchText !== null ? searchText : "";

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/roles?page=${currentPage}&pageSize=${pageSize}&search=${encodeURIComponent(finalSearch)}`,
        );

        const response = res?.data || {};

        setRoles(response.data || []);
        setTotalRecords(response.total || 0);
      } catch (err) {
        console.error(err);

        setRoles([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [instance, accounts, pageSize],
  );

  useEffect(() => {
    if (accounts.length) {
      loadRoles(debouncedSearch, page);
    }
  }, [accounts.length, debouncedSearch, page, loadRoles]);
  /* ================= HELPERS ================= */

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setForm({
      role_name: "",
      role_code: "",
      role_description: "",
    });

    setErrors({});
  };

  const validate = () => {
    const err = {};

    if (!form.role_name?.trim()) {
      err.role_name = "Role name required";
    }

    if (!form.role_code?.trim()) {
      err.role_code = "Role code required";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };

  /* ================= ACTIONS ================= */

  const handleAdd = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "POST",
        "/api/admin/roles",
        form,
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Create failed");
      }

      showToast("Role created");

      setShowAdd(false);

      resetForm();

      loadRoles(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Create failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedRole) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "PUT",
        `/api/admin/roles/${selectedRole.role_id}`,
        form,
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Update failed");
      }

      showToast("Role updated");

      setShowEdit(false);

      setSelectedRole(null);

      resetForm();

      loadRoles(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Update failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "DELETE",
        `/api/admin/roles/${selectedRole.role_id}`,
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Delete failed");
      }

      showToast("Role deleted");

      setShowDelete(false);

      setSelectedRole(null);

      if (roles.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        loadRoles(debouncedSearch, page);
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Delete failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAddRoleModal = () => {
    resetForm();
    setShowAdd(true);
  };

  const showAddTooltip = () => setIsAddTooltipVisible(true);

  const hideAddTooltip = () => setIsAddTooltipVisible(false);

  const handleAddTooltipTouchStart = () => {
    if (addTooltipTouchTimer.current) {
      clearTimeout(addTooltipTouchTimer.current);
    }
    setIsAddTooltipVisible(true);
  };

  const handleAddTooltipTouchEnd = () => {
    addTooltipTouchTimer.current = setTimeout(() => {
      setIsAddTooltipVisible(false);
    }, 1200);
  };

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapRoleCard = useCallback(
    (r) => ({
      id: `role-${r.role_id}`,
      icon: <Shield size={16} />,
      tone: "info",
      title: r.role_name,
      subtitle: r.role_code,
      meta: [
        { label: "Description", value: r.role_description || "—" },
        { label: "Status", value: "Active" },
      ],
      badges: [{ label: "Status", value: "Active", tone: "success" }],
      actions: [
        {
          label: "Edit",
          icon: <Pencil size={14} />,
          onClick: () => {
            setSelectedRole(r);
            setForm({
              role_name: r.role_name,
              role_code: r.role_code,
              role_description: r.role_description,
            });
            setShowEdit(true);
          },
        },
        {
          label: "Delete",
          icon: <Trash2 size={14} />,
          variant: "secondary",
          onClick: () => {
            setSelectedRole(r);
            setShowDelete(true);
          },
        },
      ],
    }),
    [],
  );

  const mapRoleDrawer = useCallback(
    (r) => ({
      title: r.role_name,
      fields: [
        { label: "Role Code", value: r.role_code || "—" },
        { label: "Description", value: r.role_description || "—" },
        { label: "Status", value: "Active" },
      ],
      primaryAction: {
        label: "Edit Role",
        onClick: () => {
          setSelectedRole(r);
          setForm({
            role_name: r.role_name,
            role_code: r.role_code,
            role_description: r.role_description,
          });
          setShowEdit(true);
        },
      },
    }),
    [],
  );

  const renderTable = () => (
    <>
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : (
          <ScrollableTable>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ROLE</th>
                <th style={styles.th}>CODE</th>
                <th style={styles.th}>DESCRIPTION</th>
                <th style={styles.th}>STATUS</th>
                <th style={styles.thRight}>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {roles.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      padding: "32px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "14px",
                    }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                roles.map((r) => (
                  <tr
                    key={r.role_id}
                    style={styles.row}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    <td style={styles.td}>{r.role_name}</td>

                    <td style={styles.td}>
                      <span style={styles.code}>{r.role_code}</span>
                    </td>

                    <td style={styles.td}>{r.role_description}</td>

                    <td style={styles.td}>
                      <span style={styles.active}>● Active</span>
                    </td>

                    <td style={styles.tdRight}>
                      <span
                        style={styles.icon}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "scale(1.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                        onClick={() => {
                          setSelectedRole(r);

                          setForm({
                            role_name: r.role_name,
                            role_code: r.role_code,
                            role_description: r.role_description,
                          });

                          setShowEdit(true);
                        }}
                      >
                        ✏️
                      </span>

                      <span
                        style={{
                          ...styles.icon,
                          color: "#ef4444",
                        }}
                        onClick={() => {
                          setSelectedRole(r);
                          setShowDelete(true);
                        }}
                      >
                        🗑️
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </ScrollableTable>
        )}
      </div>

      {totalRecords > 0 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalRecords / pageSize) || 1}
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPageCount={roles.length}
          onPageChange={setPage}
        />
      )}
    </>
  );

  /* ================= UI ================= */

  const paginationProps =
    totalRecords > 0
      ? {
          page,
          totalPages: Math.ceil(totalRecords / pageSize) || 1,
          totalRecords,
          pageSize,
          currentPageCount: roles.length,
          onPageChange: setPage,
        }
      : null;

  return (
    <div className="page-container" style={styles.container}>
      <div
        style={{
          ...styles.header,
          ...(isNarrowToolbar ? styles.headerNarrow : null),
        }}
      >
        <div
          style={isNarrowToolbar ? styles.headerTitleBlock : undefined}
        >
          <h2 style={styles.title}>Roles</h2>

          <p style={styles.sub}>Manage system roles and permissions</p>
        </div>

        {isNarrowToolbar ? (
          <div style={styles.addBtnWrap}>
            <button
              type="button"
              style={{
                ...styles.addBtn,
                ...styles.addBtnCompact,
              }}
              aria-label="Add role"
              aria-describedby={
                isAddTooltipVisible ? "roles-add-tooltip" : undefined
              }
              onMouseEnter={showAddTooltip}
              onMouseLeave={hideAddTooltip}
              onFocus={showAddTooltip}
              onBlur={hideAddTooltip}
              onTouchStart={handleAddTooltipTouchStart}
              onTouchEnd={handleAddTooltipTouchEnd}
              onClick={openAddRoleModal}
            >
              <ShieldPlus size={20} strokeWidth={2} aria-hidden />
            </button>
            {isAddTooltipVisible && (
              <div
                id="roles-add-tooltip"
                style={styles.addTooltip}
                role="tooltip"
              >
                <span style={styles.addTooltipArrow} aria-hidden />
                Add role
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            style={styles.addBtn}
            onClick={openAddRoleModal}
          >
            + Add role
          </button>
        )}
      </div>

      <div
        style={{
          ...styles.toolbar,
          ...(isNarrowToolbar ? styles.toolbarNarrow : null),
        }}
      >
        <input
          placeholder="Search roles"
          style={{
            ...styles.search,
            ...(isNarrowToolbar ? styles.searchNarrow : null),
          }}
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

      </div>

      <ResponsiveTableCards
        breakpoint={1024}
        data={roles}
        loading={loading}
        emptyTitle="No Roles Found"
        emptySub="No roles match your current search."
        listTitle="Roles"
        listSubtitle="Manage system roles and permissions"
        cardMapper={mapRoleCard}
        drawerMapper={mapRoleDrawer}
        pagination={paginationProps || undefined}
        renderTable={renderTable}
      />

      {/* MODAL */}

      {(showAdd || showEdit) && (
        <Modal
          title={showAdd ? "Add Role" : "Edit Role"}
          onClose={() => {
            setShowAdd(false);
            setShowEdit(false);
            resetForm();
          }}
        >
          <Input
            label="Role Name"
            value={form.role_name}
            onChange={(v) =>
              setForm({
                ...form,
                role_name: v,
              })
            }
          />

          {errors.role_name && (
            <div style={styles.error}>{errors.role_name}</div>
          )}

          <Input
            label="Role Code"
            value={form.role_code}
            onChange={(v) =>
              setForm({
                ...form,
                role_code: v,
              })
            }
          />

          {errors.role_code && (
            <div style={styles.error}>{errors.role_code}</div>
          )}

          <Input
            label="Description"
            value={form.role_description}
            onChange={(v) =>
              setForm({
                ...form,
                role_description: v,
              })
            }
          />

          <div style={styles.footer}>
            <button
              style={styles.cancelBtn}
              onClick={() => {
                setShowAdd(false);
                setShowEdit(false);
              }}
            >
              Cancel
            </button>

            <button
              style={styles.primaryBtn}
              disabled={submitting}
              onClick={showAdd ? handleAdd : handleEdit}
            >
              {submitting
                ? "Saving..."
                : showAdd
                  ? "Create role"
                  : "Update role"}
            </button>
          </div>
        </Modal>
      )}

      {showDelete && (
        <Modal title="Delete Role" onClose={() => setShowDelete(false)}>
          <p>Are you sure you want to delete this role?</p>

          <button style={styles.dangerBtn} onClick={handleDelete}>
            Delete
          </button>
        </Modal>
      )}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "error" ? "#ef4444" : "#16a34a",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* COMPONENTS */

const Input = ({ label, value, onChange }) => (
  <>
    <label style={styles.label}>{label}</label>

    <input
      style={styles.input}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </>
);

const Modal = ({ title, children, onClose }) => (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={styles.modalHeader}>
        <h3 style={{ margin: 0, fontSize: styles.title.fontSize }}>{title}</h3>

        <span onClick={onClose} style={styles.close}>
          ✕
        </span>
      </div>

      {children}
    </div>
  </div>
);

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    gap: "20px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "8px",
    fontSize: "14px",
  },

  addBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    flexShrink: 0,
  },

  headerNarrow: {
    flexWrap: "nowrap",
    alignItems: "flex-start",
  },

  headerTitleBlock: {
    flex: "1 1 0",
    minWidth: 0,
  },

  addBtnCompact: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    minWidth: "40px",
    minHeight: "40px",
  },

  addBtnWrap: {
    position: "relative",
    flexShrink: 0,
  },

  addTooltip: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "#0f172a",
    color: "#f8fafc",
    border: "1px solid #1e293b",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
    zIndex: 20,
    pointerEvents: "none",
  },

  addTooltipArrow: {
    position: "absolute",
    top: "-6px",
    right: "14px",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #0f172a",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "nowrap",
    background: "#fff",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  search: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  count: {
    fontSize: "13px",
    color: "#64748b",
    marginLeft: "auto",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  toolbarNarrow: {
    flexWrap: "wrap",
  },

  searchNarrow: {
    flex: "1 1 100%",
    width: "100%",
  },

  countNarrow: {
    flex: "1 1 100%",
    width: "100%",
    marginLeft: 0,
    textAlign: "center",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    padding: "14px 16px",
    textAlign: "right",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s ease",
  },

  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#0f172a",
    verticalAlign: "middle",
  },

  tdRight: {
    padding: "16px",
    textAlign: "right",
    verticalAlign: "middle",
  },

  code: {
    fontFamily: "monospace",
    background: "#f1f5f9",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#334155",
  },

  active: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#15803d",
    fontWeight: "500",
    fontSize: "13px",
  },

  icon: {
    cursor: "pointer",
    marginLeft: "12px",
    fontSize: "16px",
    transition: "transform 0.15s ease",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
    padding: "20px",
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "500px",
    padding: "24px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  close: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#64748b",
  },

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "6px",
    marginTop: "14px",
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "28px",
  },

  cancelBtn: {
    border: "1px solid #dbe2ea",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "500",
  },

  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
  },

  dangerBtn: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "20px",
  },

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontWeight: "500",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 1000,
  },

  error: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "6px",
  },

  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
  },

  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    borderTop: "1px solid #e2e8f0",
  },

  pageBtn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    fontWeight: "500",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
  },
};
